import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { rgba } from 'polished';
import { Grid } from 'planoplan-ui-kit/core/helpers/grid';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Button } from 'planoplan-ui-kit/core/button';
import { deviceDetected } from 'planoplan-ui-kit/core/device-detected';
import { useMediaQuery } from 'planoplan-ui-kit/core/hooks/useMediaQuery';
import { getOsData } from '@libs';
import { useTranslations } from '@libs/translations';
import { handleClickDownload } from '@modules/helper';
import { textSize, Points, marginLevel, paddingLevel } from '@styles';
import { Picture } from '@components/Picture';

export const DownloadFrame = () => {
  const { locale } = useSelector((state) => state.settings);
  const isLaptop = useMediaQuery(Points.laptop, { useSSR: true });
  const translations = useTranslations();
  const { downloadLink, os } = getOsData(locale);
  const [key, setKey] = useState('before'); // Ключ нужен что бы заново отрисовать кнопку и убрать навешанные события

  useEffect(() => {
    setKey('after');
  }, []);

  return (
    <Container>
      <Content>
        <Image>
          <Picture
            data={{
              '670': ['/images/download/download-frame-670.jpg', '/images/download/download-frame-670.webp'],
              '1x': ['/images/download/download-frame-1x.jpg', '/images/download/download-frame-1x.webp'],
              '2x': ['/images/download/download-frame-2x.jpg', '/images/download/download-frame-2x.webp'],
            }}
            alt="Download Planoplan Editor"
          />
        </Image>
        <Text>
          <Title>{translations.t(`site.download.${os}.title`)}</Title>
          <Description>{translations.t('site.downloadFrame.text')}</Description>
          <Button
            key={key}
            href={downloadLink}
            theme="secondary"
            size={isLaptop ? 'L' : 'M'}
            device={{ isMobile: deviceDetected.isMobile }}
            onClick={handleClickDownload}>
            {translations.t('site.downloadFrame.free')}
          </Button>
        </Text>
      </Content>
    </Container>
  );
};

const Container = styled.section`
  ${marginLevel('bottom', 'frog_green')};
  position: relative;
  height: 280px;
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;

  @media ${Points.mobile_only} {
    display: none;
  }

  @media ${Points.laptop} {
    height: 300px;
  }
`;

const Content = styled.div`
  ${Grid.push(2)};
  ${Grid.post(2)};
  position: relative;
  height: 100%;

  @media ${Points.laptop} {
    ${Grid.push(3)};
    ${Grid.post(3)};
  }
`;

const Image = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 10px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, ${rgba(Colors.main_blue, 0.9)} 0%, ${rgba(Colors.electric_blue, 0.9)} 100%);
  }
`;

const Text = styled.div`
  ${Grid.suffix(2, 32)};
  ${Grid.prefix(3, 32)};
  ${paddingLevel('top', 'frog_green')};
  position: relative;
  z-index: 1;
  color: ${Colors.white};
  display: flex;
  align-items: flex-start;
  flex-direction: column;

  a {
    width: 224px;
  }

  @media ${Points.laptop} {
    ${Grid.suffix(2, 30)};
    ${Grid.prefix(2, 30)};
    align-items: center;

    a {
      width: 320px;
    }
  }
`;

const Title = styled.div`
  ${textSize('h2')};
  ${marginLevel('bottom', 'azure')};
`;

const Description = styled.div`
  ${Grid.column(20, 27)};
  ${textSize('p')};
  ${marginLevel('bottom', 'whisper')};
  font-weight: 300;

  @media ${Points.laptop} {
    ${Grid.column(28, 28)};
  }
`;
